import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Leaf, ArrowLeft, Download, Printer, FileText, Calculator, PenTool, ThumbsUp, ThumbsDown, Minus, Calendar, BookOpen, BookMarked, CheckCircle, Clock, Award, Sparkles } from 'lucide-react';
import ChildSelector from '@/components/ChildSelector';
import FamilyLink from '@/components/FamilyLink';
import BrandMark from '@/components/BrandMark';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportsPage() {
  const { authHeaders, childParam, activeChild, progressVersion } = useAuth();
  const [report, setReport] = useState(null);
  const [planner, setPlanner] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/reports/evidence${childParam}`, { headers: authHeaders }),
      axios.get(`${API}/planner${childParam}`, { headers: authHeaders }).catch(() => ({ data: null })),
    ])
      .then(([r, p]) => { setReport(r.data); setPlanner(p.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [authHeaders, childParam, progressVersion]);

  const termNumber = planner?.term_number || 2;
  const termYear = planner?.term_start ? new Date(planner.term_start).getFullYear() : new Date().getFullYear();
  const termLabel = `Term ${termNumber}, ${termYear}`;

  const handleDownloadPdf = () => {
    const token = localStorage.getItem('token');
    const url = `${API}/reports/evidence/pdf${childParam}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        const safeChild = (activeChild?.name || 'student').replace(/[^a-zA-Z0-9_-]/g, '');
        a.download = `nestwise-evidence-${safeChild}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  };

  const handlePrint = () => window.print();

  const handleDownloadSubjectCertificate = (subject) => {
    const token = localStorage.getItem('token');
    const sep = childParam ? '&' : '?';
    const url = `${API}/reports/certificate/pdf${childParam}${sep}subject=${subject}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => null);
          throw new Error(err?.detail?.message || 'Could not generate certificate');
        }
        return res.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        const safeChild = (activeChild?.name || 'student').replace(/[^a-zA-Z0-9_-]/g, '');
        a.download = `nestwise-${subject}-certificate-${safeChild}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(err => alert(err.message));
  };

  const handleDownloadTermCertificate = (subject, term) => {
    const token = localStorage.getItem('token');
    const sep = childParam ? '&' : '?';
    const yearLevel = activeChild?.year_level || 5;
    const url = `${API}/reports/term-certificate/pdf${childParam}${sep}subject=${subject}&term=${term}&year=${yearLevel}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => null);
          throw new Error(err?.detail?.message || 'Could not generate certificate');
        }
        return res.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        const safeChild = (activeChild?.name || 'student').replace(/[^a-zA-Z0-9_-]/g, '');
        a.download = `nestwise-${subject}-term${term}-certificate-${safeChild}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(err => alert(err.message));
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><div className="animate-pulse text-fern font-semibold">Loading report...</div></div>;

  const s = report?.summary || {};
  const completedLessons = report?.completed_lessons || [];
  const inProgressLessons = report?.in_progress_lessons || [];
  const readingLog = report?.reading_log || [];
  const totalProgress = s.total_lessons > 0 ? Math.round((s.completed / s.total_lessons) * 100) : 0;
  // Show only the active child in the student info card — not the whole family.
  const allChildren = report?.children || [];
  const activeChildren = report?.active_child
    ? allChildren.filter(c => c.name === report.active_child)
    : allChildren;
  const displayChildren = activeChildren.length > 0 ? activeChildren : allChildren;
  const childNames = displayChildren.map(c => c.name).join(', ') || 'Student';
  const yearLevels = displayChildren.map(c => `Year ${c.year_level}`).join(', ') || '';
  const studentLabel = displayChildren.length > 1 ? 'Student(s)' : 'Student';

  const ticketIcon = (t) => {
    if (t === 'up') return <ThumbsUp className="w-4 h-4 text-green-600" />;
    if (t === 'middle') return <Minus className="w-4 h-4 text-amber-500" />;
    if (t === 'down') return <ThumbsDown className="w-4 h-4 text-red-400" />;
    return <Clock className="w-4 h-4 text-slate-400" />;
  };
  const ticketLabel = (t) => ({ up: 'Confident', middle: 'Developing', down: 'Needs Support' }[t] || 'Not recorded');

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-white/40 no-print">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <Link to="/dashboard" className="flex items-center">
            <BrandMark size={36} />
          </Link>
          <nav className="flex items-center gap-2">
            <Link to="/dashboard"><Button variant="ghost" className="text-slate-600 hover:text-fern rounded-lg gap-1"><ArrowLeft className="w-4 h-4" /> Dashboard</Button></Link>
            <FamilyLink />
            <ChildSelector />
            <Button data-testid="print-report" variant="outline" onClick={handlePrint} className="rounded-full border-slate-200 text-slate-600 gap-2">
              <Printer className="w-4 h-4" /> Print
            </Button>
            <Button data-testid="download-report-pdf" onClick={handleDownloadPdf} className="rounded-full bg-fern hover:bg-fern-light text-white gap-2">
              <Download className="w-4 h-4" /> Download PDF
            </Button>
          </nav>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Report header — print-friendly */}
        <div className="mb-8 animate-fadeInUp stagger-1">
          <div className="flex items-center gap-2 mb-2 print:hidden">
            <FileText className="w-6 h-6 text-fern" />
            <span className="text-sm text-slate-500 uppercase tracking-wider font-medium">MoE Evidence Report</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-fern-dark tracking-tight">
            Learning Evidence Report
          </h1>
          <p className="text-sm text-slate-500 italic mt-1">Pūrongo Taunakitanga Akoranga</p>
          {report?.active_child && (
            <p data-testid="report-active-child" className="text-sm text-fern mt-2 font-medium">
              Showing data for <span className="font-semibold">{report.active_child}</span>
              {activeChild?.year_level ? ` (Year ${activeChild.year_level})` : ''}
            </p>
          )}
        </div>

        {/* Student info card */}
        <Card data-testid="report-info" className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl mb-6 animate-fadeInUp stagger-2">
          <CardContent className="p-6 sm:p-8">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Family</p>
                <p className="font-heading font-semibold text-fern-dark">{report?.family_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">{studentLabel}</p>
                <p className="font-heading font-semibold text-fern-dark" data-testid="reports-student-name">{childNames}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Year Level</p>
                <p className="font-heading font-semibold text-fern-dark" data-testid="reports-year-level">{yearLevels}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Term</p>
                <p className="font-heading font-semibold text-fern-dark" data-testid="reports-term-label">{termLabel}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Progress overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 animate-fadeInUp stagger-3">
          {/* Overall */}
          <Card data-testid="overall-progress" className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <Award className="w-5 h-5 text-kowhai" />
                <h3 className="font-heading text-sm font-semibold text-slate-600">Overall Progress</h3>
              </div>
              <p className="text-4xl font-heading font-bold text-fern mb-2">{totalProgress}%</p>
              <Progress value={totalProgress} className="h-2 mb-2 bg-sand-dark [&>div]:bg-gradient-to-r [&>div]:from-fern [&>div]:to-moana" />
              <p className="text-xs text-slate-500">{s.completed || 0} of {s.total_lessons || 0} lessons completed</p>
            </CardContent>
          </Card>

          {/* Maths */}
          <Card data-testid="maths-progress" className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <Calculator className="w-5 h-5 text-moana" />
                <h3 className="font-heading text-sm font-semibold text-slate-600">Pāngarau / Maths</h3>
              </div>
              <p className="text-4xl font-heading font-bold text-moana mb-2">
                {s.maths?.total > 0 ? Math.round((s.maths.completed / s.maths.total) * 100) : 0}%
              </p>
              <Progress value={s.maths?.total > 0 ? (s.maths.completed / s.maths.total) * 100 : 0} className="h-2 mb-2 bg-moana-50 [&>div]:bg-moana" />
              <p className="text-xs text-slate-500">{s.maths?.completed || 0} of {s.maths?.total || 0} lessons</p>
            </CardContent>
          </Card>

          {/* English */}
          <Card data-testid="english-progress" className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <PenTool className="w-5 h-5 text-fern" />
                <h3 className="font-heading text-sm font-semibold text-slate-600">Reo Pākehā / English</h3>
              </div>
              <p className="text-4xl font-heading font-bold text-fern mb-2">
                {s.english?.total > 0 ? Math.round((s.english.completed / s.english.total) * 100) : 0}%
              </p>
              <Progress value={s.english?.total > 0 ? (s.english.completed / s.english.total) * 100 : 0} className="h-2 mb-2 bg-fern-50 [&>div]:bg-fern" />
              <p className="text-xs text-slate-500">{s.english?.completed || 0} of {s.english?.total || 0} lessons</p>
            </CardContent>
          </Card>
        </div>

        {/* Course Certificates — locked until each course is complete */}
        <div className="mb-8 animate-fadeInUp stagger-3" data-testid="course-certificates">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-kowhai" />
            <h2 className="font-heading text-xl font-semibold text-fern-dark">Course Certificates</h2>
            <span className="text-sm text-slate-500">— unlocked when every lesson in a course is complete</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { key: 'maths', icon: Calculator, teReo: 'Pāngarau', english: 'Mathematics',
                summary: s.maths, accent: 'moana' },
              { key: 'english', icon: PenTool, teReo: 'Reo Pākehā', english: 'English',
                summary: s.english, accent: 'fern' },
            ].map(({ key, icon: Icon, teReo, english, summary, accent }) => {
              const done = summary?.completed || 0;
              const total = summary?.total || 0;
              const pct = total > 0 ? Math.round((done / total) * 100) : 0;
              const unlocked = total > 0 && done >= total;
              return (
                <Card key={key}
                  data-testid={`certificate-card-${key}`}
                  className={`border-0 rounded-3xl overflow-hidden transition-all ${
                    unlocked
                      ? 'shadow-[0_8px_30px_-6px_rgba(201,162,39,0.35)] ring-2 ring-kowhai/40 bg-gradient-to-br from-kowhai-50 via-white to-white'
                      : 'shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] bg-white'
                  }`}
                >
                  <CardContent className="p-6 sm:p-7">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${
                          unlocked ? 'bg-kowhai/20' : `bg-${accent}-50`
                        }`}>
                          <Icon className={`w-5 h-5 ${unlocked ? 'text-kowhai-dark' : `text-${accent}`}`} />
                        </div>
                        <div>
                          <h3 className="font-heading text-lg font-bold text-fern-dark leading-tight">{teReo}</h3>
                          <p className="text-xs text-slate-500 uppercase tracking-wider">{english} · Year 5</p>
                        </div>
                      </div>
                      {unlocked ? (
                        <Badge className="bg-kowhai text-fern-dark border-0 rounded-full text-xs gap-1 font-semibold">
                          <Award className="w-3 h-3" /> UNLOCKED
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-500 border-0 rounded-full text-xs gap-1">
                          <Clock className="w-3 h-3" /> {done}/{total}
                        </Badge>
                      )}
                    </div>

                    <div className="mb-4">
                      <div className="flex items-end justify-between mb-1.5">
                        <p className="text-xs text-slate-500">{done} of {total} lessons complete</p>
                        <p className="text-sm font-heading font-bold text-fern-dark">{pct}%</p>
                      </div>
                      <Progress
                        value={pct}
                        className={`h-2 ${unlocked ? 'bg-kowhai-50 [&>div]:bg-gradient-to-r [&>div]:from-kowhai [&>div]:to-kowhai-dark' : `bg-${accent}-50 [&>div]:bg-${accent}`}`}
                      />
                    </div>

                    {unlocked ? (
                      <Button
                        data-testid={`download-certificate-${key}`}
                        onClick={() => handleDownloadSubjectCertificate(key)}
                        className="w-full rounded-full bg-kowhai hover:bg-kowhai-dark text-fern-dark font-semibold gap-2 shadow-md hover:shadow-lg transition-shadow"
                      >
                        <Download className="w-4 h-4" /> Download Certificate
                      </Button>
                    ) : (
                      <div className="rounded-2xl bg-slate-50 px-4 py-3 text-center">
                        <p className="text-xs text-slate-500 leading-relaxed">
                          Complete <span className="font-semibold text-fern-dark">{Math.max(total - done, 0)}</span> more {teReo} {Math.max(total - done, 0) === 1 ? 'lesson' : 'lessons'} to unlock this certificate.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Term Certificates — unlocked per subject, per term, scoped to the
            active child's year level. summary.terms is keyed "{year}-{term}"
            so a Year 6 child never sees Year 5's term totals folded in. */}
        {(() => {
          const childYear = activeChild?.year_level || 5;
          const yearTermEntries = Object.entries(s.terms || {})
            .filter(([key]) => Number(key.split('-')[0]) === childYear)
            .map(([key, data]) => ({ termNum: Number(key.split('-')[1]), data }))
            .sort((a, b) => a.termNum - b.termNum);
          if (yearTermEntries.length === 0) return null;
          return (
          <div className="mb-8 animate-fadeInUp stagger-3" data-testid="term-certificates">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Calendar className="w-5 h-5 text-moana" />
              <h2 className="font-heading text-xl font-semibold text-fern-dark">Term Certificates</h2>
              <span className="text-sm text-slate-500">— unlock one when every lesson of a subject in that term is complete</span>
            </div>
            <div className="space-y-5">
              {yearTermEntries.map(({ termNum, data: termData }) => {
                const termKey = termNum;
                return (
                  <div key={termKey} data-testid={`term-${termKey}-certificates`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className="bg-moana/10 text-moana-dark border border-moana/20 rounded-full text-xs font-semibold px-3 py-1">
                        TERM {termKey}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        { key: 'maths', icon: Calculator, teReo: 'Pāngarau', english: 'Mathematics', accent: 'moana' },
                        { key: 'english', icon: PenTool, teReo: 'Reo Pākehā', english: 'English', accent: 'fern' },
                      ].map(({ key, icon: Icon, teReo, english, accent }) => {
                        const breakdown = termData[key] || { completed: 0, total: 0 };
                        const done = breakdown.completed;
                        const total = breakdown.total;
                        const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                        const unlocked = total > 0 && done >= total;
                        return (
                          <Card
                            key={`${termKey}-${key}`}
                            data-testid={`term-certificate-card-${termKey}-${key}`}
                            className={`border-0 rounded-2xl overflow-hidden transition-all ${
                              unlocked
                                ? 'shadow-[0_6px_24px_-6px_rgba(201,162,39,0.30)] ring-2 ring-kowhai/40 bg-gradient-to-br from-kowhai-50 via-white to-white'
                                : 'shadow-[0_2px_12px_-2px_rgba(0,0,0,0.05)] bg-white'
                            }`}
                          >
                            <CardContent className="p-5">
                              <div className="flex items-start justify-between gap-3 mb-3">
                                <div className="flex items-center gap-2.5">
                                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                                    unlocked ? 'bg-kowhai/20' : `bg-${accent}-50`
                                  }`}>
                                    <Icon className={`w-4 h-4 ${unlocked ? 'text-kowhai-dark' : `text-${accent}`}`} />
                                  </div>
                                  <div>
                                    <h3 className="font-heading text-base font-bold text-fern-dark leading-tight">{teReo}</h3>
                                    <p className="text-[11px] text-slate-500 uppercase tracking-wider">Term {termKey} · {english}</p>
                                  </div>
                                </div>
                                {unlocked ? (
                                  <Badge className="bg-kowhai text-fern-dark border-0 rounded-full text-[10px] gap-1 font-semibold">
                                    <Award className="w-3 h-3" /> UNLOCKED
                                  </Badge>
                                ) : (
                                  <Badge className="bg-slate-100 text-slate-500 border-0 rounded-full text-[10px] gap-1">
                                    <Clock className="w-3 h-3" /> {done}/{total}
                                  </Badge>
                                )}
                              </div>
                              <div className="mb-3">
                                <div className="flex items-end justify-between mb-1.5">
                                  <p className="text-[11px] text-slate-500">{done} of {total} lessons</p>
                                  <p className="text-xs font-heading font-bold text-fern-dark">{pct}%</p>
                                </div>
                                <Progress
                                  value={pct}
                                  className={`h-1.5 ${unlocked ? 'bg-kowhai-50 [&>div]:bg-gradient-to-r [&>div]:from-kowhai [&>div]:to-kowhai-dark' : `bg-${accent}-50 [&>div]:bg-${accent}`}`}
                                />
                              </div>
                              {unlocked ? (
                                <Button
                                  data-testid={`download-term-certificate-${termKey}-${key}`}
                                  onClick={() => handleDownloadTermCertificate(key, termNum)}
                                  size="sm"
                                  className="w-full rounded-full bg-kowhai hover:bg-kowhai-dark text-fern-dark font-semibold gap-2 shadow-sm hover:shadow transition-shadow text-xs"
                                >
                                  <Download className="w-3.5 h-3.5" /> Download Term {termKey} Certificate
                                </Button>
                              ) : total > 0 ? (
                                <div className="rounded-xl bg-slate-50 px-3 py-2 text-center">
                                  <p className="text-[11px] text-slate-500 leading-relaxed">
                                    <span className="font-semibold text-fern-dark">{total - done}</span> more {teReo} {total - done === 1 ? 'lesson' : 'lessons'} in Term {termKey}
                                  </p>
                                </div>
                              ) : (
                                <div className="rounded-xl bg-slate-50 px-3 py-2 text-center">
                                  <p className="text-[11px] text-slate-400 italic">No lessons yet</p>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          );
        })()}

        {/* Self-assessment summary */}
        <Card data-testid="exit-ticket-summary" className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl mb-8 animate-fadeInUp stagger-4">
          <CardContent className="p-6 sm:p-8">
            <h2 className="font-heading text-lg font-semibold text-fern-dark mb-4">Self-Assessment Summary (Exit Tickets)</h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-green-50 rounded-2xl p-4 text-center">
                <ThumbsUp className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-heading font-bold text-green-700">{s.exit_tickets?.up || 0}</p>
                <p className="text-xs text-green-600">Confident</p>
              </div>
              <div className="bg-amber-50 rounded-2xl p-4 text-center">
                <Minus className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                <p className="text-2xl font-heading font-bold text-amber-600">{s.exit_tickets?.middle || 0}</p>
                <p className="text-xs text-amber-500">Developing</p>
              </div>
              <div className="bg-red-50 rounded-2xl p-4 text-center">
                <ThumbsDown className="w-6 h-6 text-red-400 mx-auto mb-2" />
                <p className="text-2xl font-heading font-bold text-red-500">{s.exit_tickets?.down || 0}</p>
                <p className="text-xs text-red-400">Needs Support</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Worth another look
            The counters above say how many lessons were hard. This says which,
            and why, so the number turns into something a parent can act on. */}
        {(report?.needs_review || []).length > 0 && (
          <Card data-testid="needs-review" className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl mb-8 animate-fadeInUp stagger-4">
            <CardContent className="p-6 sm:p-8">
              <h2 className="font-heading text-lg font-semibold text-fern-dark">Worth another look</h2>
              <p className="text-sm text-slate-500 mt-1 mb-5">
                Lessons {activeChild?.name || 'your child'} marked as not yet, or answered two or more
                questions wrong in. Open one to reteach it.
              </p>
              <ul className="divide-y divide-slate-100">
                {report.needs_review.map((r) => (
                  <li key={r.reference} className="py-3 first:pt-0 last:pb-0">
                    <Link
                      to={`/lessons/${r.reference}`}
                      data-testid={`needs-review-${r.reference}`}
                      className="group flex items-start justify-between gap-4 rounded-xl -mx-2 px-2 py-1 hover:bg-fern/5 transition-colors"
                    >
                      <span className="min-w-0">
                        <span className="block font-medium text-fern-dark group-hover:text-fern truncate">
                          {r.title}
                        </span>
                        <span className="block text-xs text-slate-500 mt-0.5">
                          {r.subject === 'maths' ? 'Pāngarau' : 'Reo Pākehā'} · Year {r.year} Term {r.term} Week {r.week}
                        </span>
                      </span>
                      <span className="flex flex-wrap justify-end gap-1.5 shrink-0">
                        {r.exitTicket === 'down' && (
                          <Badge className="bg-red-50 text-red-600 border-0 rounded-full text-xs font-medium">
                            Marked not yet
                          </Badge>
                        )}
                        {r.wrongCount > 0 && (
                          <Badge className="bg-amber-50 text-amber-700 border-0 rounded-full text-xs font-medium tabular-nums">
                            {r.wrongCount}/{r.answeredCount} wrong
                          </Badge>
                        )}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* NZ Curriculum areas covered */}
        {(s.curriculum_areas_covered || []).length > 0 && (
          <Card data-testid="curriculum-areas" className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl mb-8 animate-fadeInUp stagger-5">
            <CardContent className="p-6 sm:p-8">
              <h2 className="font-heading text-lg font-semibold text-fern-dark mb-4">NZ Curriculum Areas Covered</h2>
              <div className="space-y-2">
                {s.curriculum_areas_covered.map((area, i) => (
                  <div key={i} className="flex items-start gap-3 bg-fern-50 rounded-xl p-3">
                    <CheckCircle className="w-4 h-4 text-fern mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-fern-dark">{area}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Completed lessons detail */}
        <div className="mb-8">
          <h2 className="font-heading text-xl font-semibold text-fern-dark mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Completed Lessons — Detailed Evidence
          </h2>
          {completedLessons.length === 0 ? (
            <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl">
              <CardContent className="p-8 text-center">
                <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No lessons completed yet. Complete lessons to see evidence here.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {completedLessons.map((lesson, i) => (
                <Card key={lesson.reference} data-testid={`evidence-${lesson.reference}`}
                  className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl animate-fadeInUp"
                  style={{ animationDelay: `${i * 60}ms` }}>
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Badge className={`${lesson.subject === 'maths' ? 'bg-moana-50 text-moana' : 'bg-fern-50 text-fern'} border-0 rounded-full text-xs gap-1`}>
                          {lesson.subject === 'maths' ? <Calculator className="w-3 h-3" /> : <PenTool className="w-3 h-3" />}
                          {lesson.subject.charAt(0).toUpperCase() + lesson.subject.slice(1)}
                        </Badge>
                        <Badge className="bg-sand-dark text-slate-600 border-0 rounded-full text-xs">
                          Week {lesson.week} Day {lesson.day}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {ticketIcon(lesson.exitTicket)}
                        <span className="text-xs text-slate-500">{ticketLabel(lesson.exitTicket)}</span>
                      </div>
                    </div>

                    <h3 className="font-heading text-lg font-semibold text-fern-dark mb-0.5">{lesson.title}</h3>
                    <p className="text-xs text-slate-400 italic mb-3">{lesson.teReoTitle}</p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Learning Objective</p>
                        <p className="text-sm text-slate-700">{lesson.learningObjective}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">NZ Curriculum Link</p>
                        <p className="text-sm text-slate-700">{lesson.nzCurriculumLink}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                      <div className="bg-slate-50 rounded-xl p-3">
                        <p className="text-[10px] text-slate-400 uppercase">Duration</p>
                        <p className="text-sm font-semibold text-slate-700">{lesson.duration} min</p>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-3">
                        <p className="text-[10px] text-slate-400 uppercase">Completed</p>
                        <p className="text-sm font-semibold text-slate-700">{lesson.completedAt ? lesson.completedAt.slice(0, 10) : '—'}</p>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-3">
                        <p className="text-[10px] text-slate-400 uppercase">Self-Assessment</p>
                        <p className="text-sm font-semibold text-slate-700 flex items-center gap-1">{ticketIcon(lesson.exitTicket)} {ticketLabel(lesson.exitTicket)}</p>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-3">
                        <p className="text-[10px] text-slate-400 uppercase">Subject</p>
                        <p className="text-sm font-semibold text-slate-700">{lesson.subject.charAt(0).toUpperCase() + lesson.subject.slice(1)}</p>
                      </div>
                    </div>

                    {lesson.evidenceNotes && (
                      <div className="bg-kowhai-50 rounded-2xl p-4 border-l-4 border-kowhai">
                        <p className="text-xs text-kowhai-dark uppercase tracking-wider mb-1 font-semibold">Parent/Whānau Evidence Notes</p>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{lesson.evidenceNotes}</p>
                      </div>
                    )}

                    {lesson.evidencePhotoCount > 0 && (
                      <div data-testid={`photo-count-${lesson.reference}`} className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-fern-50 rounded-full text-xs text-fern-dark">
                        <span className="w-2 h-2 rounded-full bg-fern" />
                        {lesson.evidencePhotoCount} evidence photo{lesson.evidencePhotoCount > 1 ? 's' : ''} attached — included in PDF report
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* In-progress lessons */}
        {inProgressLessons.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-fern-dark mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              In Progress
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {inProgressLessons.map(lesson => (
                <Card key={lesson.reference} data-testid={`inprogress-${lesson.reference}`}
                  className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={`${lesson.subject === 'maths' ? 'bg-moana-50 text-moana' : 'bg-fern-50 text-fern'} border-0 rounded-full text-xs`}>
                        W{lesson.week}D{lesson.day}
                      </Badge>
                      <Badge className="bg-amber-100 text-amber-700 border-0 rounded-full text-xs">In Progress</Badge>
                    </div>
                    <p className="font-heading font-semibold text-fern-dark text-sm">{lesson.title}</p>
                    <p className="text-xs text-slate-400 mt-1">Current phase: {lesson.phase || 'Started'}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Reading log */}
        {readingLog.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-fern-dark mb-4 flex items-center gap-2">
              <BookMarked className="w-5 h-5 text-fern" />
              Reading Log <span className="text-sm font-normal text-slate-400">({readingLog.length} {readingLog.length === 1 ? 'book' : 'books'})</span>
            </h2>
            <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl">
              <CardContent className="p-0 overflow-hidden">
                <div className="divide-y divide-slate-100">
                  {readingLog.map(r => (
                    <div key={r.id} data-testid={`report-reading-${r.id}`} className="p-5">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading font-semibold text-fern-dark">{r.title}</h3>
                          {r.author && <p className="text-sm text-slate-500 italic">by {r.author}</p>}
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs">
                          {r.date_finished && <Badge className="bg-fern-50 text-fern-dark border-0 rounded-full">Finished {r.date_finished}</Badge>}
                          {r.pages && <Badge className="bg-moana-50 text-moana-dark border-0 rounded-full">{r.pages} pages</Badge>}
                          {r.difficulty && <Badge className="bg-kowhai-50 text-kowhai-dark border-0 rounded-full capitalize">{r.difficulty.replace('_', ' ')}</Badge>}
                        </div>
                      </div>
                      {(r.child_response || r.parent_notes) && (
                        <div className="mt-3 space-y-2">
                          {r.child_response && (
                            <p className="text-sm text-slate-700"><span className="text-xs uppercase tracking-wider text-fern-dark font-semibold">{report?.active_child || 'Child'} said:</span> {r.child_response}</p>
                          )}
                          {r.parent_notes && (
                            <p className="text-sm text-slate-700"><span className="text-xs uppercase tracking-wider text-kowhai-dark font-semibold">Parent notes:</span> {r.parent_notes}</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Print footer */}
        <div className="mt-12 pt-6 border-t border-slate-200 text-center">
          <p className="text-xs text-slate-400">
            This report was generated by NestWise — a curriculum-aligned learning platform for NZ homeschooling families.
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Generated on {new Date().toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' })}. For Ministry of Education review purposes.
          </p>
          <div className="mt-6 print:block hidden">
            <p className="text-sm text-slate-600">Parent/Guardian Signature: ____________________________&nbsp;&nbsp;&nbsp;&nbsp;Date: ______________</p>
          </div>
        </div>

        {/* Download bar (sticky bottom, no-print) */}
        <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-slate-200 py-3 px-4 no-print">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-fern">{s.completed || 0}</span> lessons completed
              {s.completed > 0 && <span className="text-slate-400"> — ready for MoE review</span>}
            </p>
            <div className="flex gap-2">
              <Button data-testid="bottom-print" variant="outline" onClick={handlePrint} className="rounded-full border-slate-200 text-slate-600 gap-2">
                <Printer className="w-4 h-4" /> Print Report
              </Button>
              <Button data-testid="bottom-download-pdf" onClick={handleDownloadPdf} className="rounded-full bg-fern hover:bg-fern-light text-white gap-2">
                <Download className="w-4 h-4" /> Download PDF
              </Button>
            </div>
          </div>
        </div>

        {/* Bottom spacer for sticky bar */}
        <div className="h-20 no-print" />
      </main>
    </div>
  );
}
